import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@powersync/react';
import { useClinicCtx } from '@/app/state';
import { Button, Card, Empty, Modal, Pill } from '@/components/ui';
import { getPatientIndex, ageLabel, loadPatientForEdit, type PatientForEdit } from '@/features/patients/repo';
import { RegisterPatientForm } from '@/features/patients/RegisterPatientForm';
import { useApp } from '@/app/state';
import { IssueCertificate } from '@/features/documents/IssueCertificate';
import { CertificatePrint } from '@/features/documents/CertificatePrint';
import { cancelDocument, countPrint, getDocument, listDocuments, type DocumentRow } from '@/features/documents/repo';
import type { PatientIndexRow } from '@/lib/db/schema';
import { formatPhoneLocal } from '@/lib/myanmar/phone';
import { formatDate } from '@/lib/myanmar/digits';

export function PatientPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const { clinic } = useApp();
  const [p, setP] = useState<PatientIndexRow | null>(null);
  const [allergies, setAllergies] = useState<string[]>([]);
  const [editing, setEditing] = useState<PatientForEdit | null>(null);
  const [version, setVersion] = useState(0);
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [issuing, setIssuing] = useState(false);
  const [showDoc, setShowDoc] = useState<DocumentRow | null>(null);
  const [doctor, setDoctor] = useState<{ id: string; name_en: string | null; name_my: string | null; mmc_reg_no: string | null } | null>(null);
  useEffect(() => {
    if (!id) return;
    void getPatientIndex(ctx.db, id).then(setP);
    void ctx.db.getAll<{ id: string; enc: string; severity: string | null }>('select id, enc, severity from patient_allergies where patient_id = ? and active = 1', [id])
      .then(async (rows) => setAllergies(await Promise.all(rows.map(async (r) => (await ctx.crypto.decrypt<{ substance: string }>(r.id, r.enc)).substance + (r.severity ? ` (${r.severity})` : '')))));
    void listDocuments(ctx, id).then(setDocs);
    void ctx.db.getOptional<{ id: string; name_en: string | null; name_my: string | null; mmc_reg_no: string | null }>(
      'select id, name_en, name_my, mmc_reg_no from doctors where clinic_id = ? and active = 1 order by name_en limit 1', [ctx.clinicId]).then(setDoctor);
  }, [id, ctx, version]);
  const { data: visits } = useQuery<{ id: string; token_label: string; status: string; issued_at: string; doctor_name_en: string | null; doctor_name_my: string | null; session_date: string }>(
    `select t.id, t.token_label, t.status, t.issued_at, d.name_en as doctor_name_en, d.name_my as doctor_name_my, s.session_date from tokens t join sessions s on s.id = t.session_id join doctors d on d.id = s.doctor_id where t.patient_id = ? order by t.issued_at desc limit 50`, [id ?? '']);

  if (!p) return <Empty>{t('common.loading')}</Empty>;
  const age = ageLabel(p);
  const phones = JSON.parse(p.phones ?? '[]') as string[];
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
      <Card>
        <div className="flex items-center justify-between"><span className="text-sm text-muted">{p.patient_no}</span>
          <Button className="py-1" onClick={async () => setEditing(await loadPatientForEdit(ctx, p.patient_id!))}>{t('common.edit')}</Button></div>
        <h1 className="text-2xl font-bold">{p.honorific && p.honorific !== 'none' ? t(`honorific.${p.honorific}`) + ' ' : ''}{my ? (p.name_my ?? p.name_en) : (p.name_en ?? p.name_my)}</h1>
        <div className="text-ink-2">{my ? p.name_en : p.name_my}</div>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">{t('patient.age')}</dt><dd>{age ? `${age.years} ${t('patient.ageYears')}${age.years < 5 ? ` ${age.months} ${t('patient.ageMonths')}` : ''}` : '—'}</dd>
          <dt className="text-muted">{t('patient.phone')}</dt><dd>{phones.map(formatPhoneLocal).join(', ') || '—'}</dd>
          <dt className="text-muted">{t('patient.nrc')}</dt><dd>{p.nrc ?? '—'}</dd>
          <dt className="text-muted">{t('patient.allergies')}</dt><dd>{allergies.length ? allergies.map((a) => <Pill key={a} tone="danger">{a}</Pill>) : t('common.none')}</dd>
        </dl>
      </Card>
      <Card>
        <h2 className="mb-2 font-bold">{t('patient.visits', { count: (visits ?? []).filter((v) => v.status === 'done').length })}</h2>
        {(visits ?? []).length === 0 ? <Empty>{t('common.none')}</Empty> : (
          <ul className="divide-y divide-rule text-sm">
            {(visits ?? []).map((v) => (
              <li key={v.id} className="flex items-center justify-between py-2">
                <span>{v.session_date} · {my ? (v.doctor_name_my ?? v.doctor_name_en) : (v.doctor_name_en ?? v.doctor_name_my)}</span>
                <span className="flex items-center gap-2"><span className="font-mono">{v.token_label}</span><Pill tone={v.status === 'done' ? 'accent' : 'neutral'}>{t(`queue.${v.status}`)}</Pill></span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/reception" className="mt-3 inline-block text-sm text-accent-ink underline">{t('nav.reception')}</Link>
      </Card>

      <Card className="lg:col-span-2">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-bold">{t('doc.documents')}</h2>
          <Button className="py-1" onClick={() => setIssuing(true)}>＋ {t('doc.issue')}</Button>
        </div>
        {docs.length === 0 ? <p className="m-0 text-muted">{t('doc.none')}</p> : (
          <ul className="divide-y divide-rule text-sm">
            {docs.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <b>{t(`doc.${d.kind === 'medical_certificate' ? 'certificate' : d.kind}`)}</b>
                  <span className="ml-2 font-mono text-xs text-ink-2">{d.doc_no}</span>
                  <div className="text-xs text-ink-2">
                    {formatDate(d.issued_at, my ? 'my' : 'en')}
                    {d.rest_days ? ` · ${t('doc.days', { count: d.rest_days })}` : ''}
                    {d.print_count > 0 ? ` · ${t('doc.printedTimes', { n: d.print_count })}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {d.status === 'cancelled' && <Pill tone="danger">{t('doc.cancelled')}</Pill>}
                  <Button className="py-1" onClick={() => setShowDoc(d)}>{t('doc.reprint')}</Button>
                  {d.status !== 'cancelled' && (
                    <button className="text-xs text-ink-2 underline hover:text-danger" onClick={async () => {
                      const reason = window.prompt(t('doc.cancelReason') ?? '');
                      if (reason && reason.trim()) { await cancelDocument(ctx, d.id, reason.trim()); setVersion((v) => v + 1); }
                    }}>{t('doc.cancel')}</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal open={issuing} onClose={() => setIssuing(false)} title={t('doc.issue')} wide>
        <IssueCertificate
          patientId={p.patient_id!} doctorId={doctor?.id ?? null} prefix={clinic?.doc_no_prefix ?? 'MC'}
          onCancel={() => setIssuing(false)}
          onIssued={async (docId) => { setIssuing(false); setVersion((v) => v + 1); setShowDoc(await getDocument(ctx, docId)); }}
        />
      </Modal>

      <Modal open={!!showDoc} onClose={() => setShowDoc(null)} title={showDoc ? t(`doc.${showDoc.kind === 'medical_certificate' ? 'certificate' : showDoc.kind}`) : ''} wide>
        {showDoc && (
          <CertificatePrint
            doc={showDoc} patient={p} doctorName={my ? (doctor?.name_my ?? doctor?.name_en ?? null) : (doctor?.name_en ?? doctor?.name_my ?? null)}
            doctorRegNo={doctor?.mmc_reg_no ?? null}
            clinicName={(my && clinic?.name_my) || clinic?.name || ''} clinicNameAlt={my ? clinic?.name ?? null : clinic?.name_my ?? null}
            clinicPhone={clinic?.phone ?? null} clinicAddress={clinic?.address_line ?? null}
            onPrinted={() => { void countPrint(ctx, showDoc.id); setVersion((v) => v + 1); }}
            onClose={() => setShowDoc(null)}
          />
        )}
      </Modal>
      <Modal open={!!editing} onClose={() => setEditing(null)} title={t('patient.edit')} wide>
        {editing && <RegisterPatientForm prefix={clinic?.patient_no_prefix ?? 'KLX'} initial={editing} onDone={() => { setEditing(null); setVersion((v) => v + 1); }} />}
      </Modal>
    </div>
  );
}
